import { useEffect, useMemo, useState } from 'react'

import {
  consumePendingReportDataIssueOpen,
  COP_REPORT_DATA_ISSUE_OPEN_EVENT,
  COP_REPORT_DATA_ISSUE_SUBMIT_EVENT,
  ReportDataIssueOpenDetail,
  ReportDataIssueSubmitDetail,
} from '../../constants/events'
import { ToastService } from '../../services/toast.service'
import PopupContainer from '../Shared/PopupContainer/PopupContainer'
import {
  ActiveContext,
  CopReportDataIssueProps,
  getMaxLength,
  parseConfig,
} from './CopReportDataIssue.config'
import {
  MainPopupContent,
  MainPopupFooter,
  UnsavedConfirmFooter,
} from './CopReportDataIssue.view'
import { reportDataIssue } from './services/CopReportDataIssue.service'

type ValidationResult = {
  reasonError: string
  descriptionError: string
  trimmedDescription: string
}

type ValidationInput = {
  reasonKeys: string[]
  hasReasonOptions: boolean
  description: string
  maxLength: number
}

type ValidationMessages = {
  reasonMessage: string
  descriptionRequiredMessage: string
  descriptionMaxMessage: string
}

function validateInputs(
  input: ValidationInput,
  messages: ValidationMessages,
): ValidationResult {
  const trimmedDescription = input.description.trim()
  const reasonError = input.hasReasonOptions && input.reasonKeys.length === 0
    ? messages.reasonMessage
    : ''

  if (!trimmedDescription) {
    return {
      reasonError,
      descriptionError: messages.descriptionRequiredMessage,
      trimmedDescription,
    }
  }

  if (trimmedDescription.length > input.maxLength) {
    return {
      reasonError,
      descriptionError: messages.descriptionMaxMessage,
      trimmedDescription,
    }
  }

  return {
    reasonError,
    descriptionError: '',
    trimmedDescription,
  }
}

function mapOpenEventToContext(detail?: ReportDataIssueOpenDetail): ActiveContext {
  return {
    type: detail?.contextType === 'asset' ? 'asset' : 'site',
    id: detail?.contextId,
    name: detail?.contextName,
  }
}

export default function CopReportDataIssue(props: Readonly<CopReportDataIssueProps>) {
  const rawConfig = props.configJson || props['config-json'] || ''
  const config = useMemo(() => parseConfig(rawConfig), [rawConfig])
  const maxDescriptionLength = getMaxLength(config.maxDescriptionLength)

  const [isOpen, setIsOpen] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false)
  const [selectedReasonKeys, setSelectedReasonKeys] = useState<string[]>([])
  const [description, setDescription] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [descriptionError, setDescriptionError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [activeContext, setActiveContext] = useState<ActiveContext>({ type: 'site' })

  const reasons = useMemo(() => {
    return (config.reasons ?? []).filter(reason => reason?.key?.trim() && reason?.value?.trim())
  }, [config.reasons])

  const resetForm = () => {
    setSelectedReasonKeys([])
    setDescription('')
    setReasonError('')
    setDescriptionError('')
    setShowSuccess(false)
    setShowUnsavedConfirm(false)
    setIsSubmitting(false)
  }

  const closeAll = () => {
    setIsOpen(false)
    setShowUnsavedConfirm(false)
    setShowSuccess(false)
  }

  const hasUnsavedChanges = selectedReasonKeys.length > 0 || description.trim().length > 0

  const requestClose = () => {
    if (!showSuccess && hasUnsavedChanges) {
      setShowUnsavedConfirm(true)
      return
    }
    closeAll()
  }

  useEffect(() => {
    const openWithDetail = (detail?: ReportDataIssueOpenDetail): void => {
      setActiveContext(mapOpenEventToContext(detail))
      resetForm()
      setIsOpen(true)
    }

    const onOpen = (event: Event): void => {
      const detail = (event as CustomEvent<ReportDataIssueOpenDetail>).detail
      openWithDetail(detail)
      consumePendingReportDataIssueOpen()
    }

    window.addEventListener(COP_REPORT_DATA_ISSUE_OPEN_EVENT, onOpen)

    const pendingOpen = consumePendingReportDataIssueOpen()
    if (pendingOpen) {
      openWithDetail(pendingOpen)
    }

    return () => window.removeEventListener(COP_REPORT_DATA_ISSUE_OPEN_EVENT, onOpen)
  }, [])

  const toggleReason = (key: string) => {
    setSelectedReasonKeys(prev => {
      const exists = prev.includes(key)
      const next = exists ? prev.filter(item => item !== key) : [...prev, key]
      if (next.length > 0) {
        setReasonError('')
      }
      return next
    })
  }

  const onDescriptionChange = (value: string) => {
    setDescription(value)
    if (value.trim()) {
      setDescriptionError('')
    }
  }

  const submitReport = async () => {
    const validation = validateInputs(
      {
        reasonKeys: selectedReasonKeys,
        hasReasonOptions: reasons.length > 0,
        description,
        maxLength: maxDescriptionLength,
      },
      {
        reasonMessage: config.reasonsRequiredMessage || '',
        descriptionRequiredMessage: config.descriptionRequiredMessage || '',
        descriptionMaxMessage: config.descriptionMaxLengthMessage || '',
      },
    )

    setReasonError(validation.reasonError)
    setDescriptionError(validation.descriptionError)

    if (validation.reasonError || validation.descriptionError) {
      return
    }

    const contextId = activeContext.id?.trim()

    if (!contextId) {
      ToastService.error('Unable to report data issue because context ID is missing.')
      return
    }

    setIsSubmitting(true)

    try {
      await reportDataIssue({
        contextType: activeContext.type,
        contextId,
        description: validation.trimmedDescription,
        issueCategories: selectedReasonKeys,
      })

      const submitDetail: ReportDataIssueSubmitDetail = {
        contextType: activeContext.type,
        contextId,
        contextName: activeContext.name,
        reasonKeys: selectedReasonKeys,
        description: validation.trimmedDescription,
      }

      window.dispatchEvent(new CustomEvent(COP_REPORT_DATA_ISSUE_SUBMIT_EVENT, { detail: submitDetail }))
      setShowSuccess(true)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to report data issue. Please try again.'
      ToastService.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const title = config.dialogTitle || ''
  const subtitle = activeContext.name || ''

  return (
    <>
      <PopupContainer
        isOpen={isOpen}
        title={showSuccess ? config.confirmation?.title || '' : title}
        subtitle={!showSuccess && subtitle ? subtitle : undefined}
        className="cop-report-data-issue"
        onClose={requestClose}
        footer={
          <MainPopupFooter
            showSuccess={showSuccess}
            activeContext={activeContext}
            config={config}
            isSubmitting={isSubmitting}
            onCloseAll={closeAll}
            onRequestClose={requestClose}
            onSubmit={submitReport}
          />
        }
      >
        <MainPopupContent
          showSuccess={showSuccess}
          activeContext={activeContext}
          config={config}
          reasons={reasons}
          selectedReasonKeys={selectedReasonKeys}
          reasonError={reasonError}
          description={description}
          descriptionError={descriptionError}
          maxDescriptionLength={maxDescriptionLength}
          onToggleReason={toggleReason}
          onDescriptionChange={onDescriptionChange}
        />
      </PopupContainer>

      <PopupContainer
        isOpen={showUnsavedConfirm}
        title={config.unsavedChanges?.message || ''}
        className="cop-report-data-issue cop-report-data-issue--confirm"
        onClose={() => setShowUnsavedConfirm(false)}
        footer={
          <UnsavedConfirmFooter
            config={config}
            onConfirmClose={closeAll}
            onCancelClose={() => setShowUnsavedConfirm(false)}
          />
        }
      >
        <p className="cop-report-data-issue__text">
          {config.unsavedChanges?.subtext || ''}
        </p>
      </PopupContainer>
    </>
  )
}
